import { useFeatureIsOn } from '@frontend/react';
import { useAuthentication } from '@frontend/web/hooks/useAuthentication';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  checkTransferNeeded,
  ssoCallbackInitialState,
  ssoCallbackStateMachine,
  type SSOCallbackState,
} from './ssoCallbackStateMachine';

const TIMEOUT_MS = 30_000;

type UseSSOCallbackOptions = {
  isMCPFlow: boolean;
  onNavigateToMCPComplete: () => void;
  urlParams?: {
    inviteId?: string;
    promo?: string;
    referrer?: string;
  };
};

type UseSSOCallbackReturn = {
  handleCompleteProfile: (data: { firstName: string; lastName: string }) => Promise<void>;
  isNewSignUp: boolean;
  state: SSOCallbackState;
};

export const useSSOCallback = ({
  isMCPFlow,
  onNavigateToMCPComplete,
  urlParams,
}: UseSSOCallbackOptions): UseSSOCallbackReturn => {
  const { clearURLParamData, getURLParamData, sso } = useAuthentication();
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');
  const [state, dispatch] = useReducer(ssoCallbackStateMachine, ssoCallbackInitialState);
  const hasStartedCallbackRef = useRef(false);

  const { handleTransferToSignIn, handleTransferToSignUp, login, session, setActive, signIn, signUp, updateSignUp } =
    sso;

  // Note: ssoEnabled feature flag check removed — if the user reached /sso-callback,
  // they already clicked an SSO button (only visible when the flag is enabled).
  // The old check redirected to /login before flags loaded, breaking the callback.

  useEffect(() => {
    if (session || state.step === 'error' || state.step === 'navigating') return;

    const timeoutId = setTimeout(() => {
      dispatch({ type: 'timeout' });
    }, TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [session, state.step]);

  useEffect(() => {
    if (!sso.isLoaded || hasStartedCallbackRef.current) return;
    if (state.step !== 'initializing') return;

    // eslint-disable-next-line fp/no-mutation
    hasStartedCallbackRef.current = true;

    const transfer = checkTransferNeeded(signIn, signUp);
    if (transfer.needsTransfer && transfer.direction) {
      dispatch({ type: 'ssoLoaded' });
      dispatch({ direction: transfer.direction, type: 'needsTransfer' });
    } else {
      dispatch({ type: 'ssoLoaded' });
    }
  }, [sso.isLoaded, state.step, signIn, signUp]);

  useEffect(() => {
    const performTransfer = async () => {
      if (state.step !== 'transferring') return;

      try {
        const transfer = checkTransferNeeded(signIn, signUp);
        if (transfer.direction === 'toSignIn') {
          await handleTransferToSignIn();
        } else if (transfer.direction === 'toSignUp') {
          await handleTransferToSignUp();
        }
        dispatch({ type: 'transferComplete' });
      } catch {
        dispatch({ error: 'Authentication failed', type: 'transferFailed' });
      }
    };

    performTransfer();
  }, [state.step, signIn, signUp, handleTransferToSignIn, handleTransferToSignUp]);

  useEffect(() => {
    if (state.step === 'checkingTransfer' && session) {
      dispatch({ type: 'sessionReady' });
    }
  }, [state.step, session]);

  useEffect(() => {
    const processSession = async () => {
      if (state.step !== 'processingSession' || !session) return;

      const hasMissingRequirements =
        sso.signUp?.status === 'missing_requirements' && sso.signUp.missingFields.length > 0;

      if (isMCPFlow) {
        clearURLParamData();
        dispatch({ type: 'navigateToMCPComplete' });
        onNavigateToMCPComplete();
        return;
      }

      if (hasMissingRequirements) {
        dispatch({ type: 'showProfileForm' });
        return;
      }

      const storedParams = getURLParamData() ?? {};
      const inviteId = urlParams?.inviteId ?? storedParams.inviteId;
      const promo = urlParams?.promo ?? storedParams.promo;
      const referrer = urlParams?.referrer ?? storedParams.referrer;
      const { redirect } = storedParams;
      dispatch({ type: 'startAuthCompletion' });
      try {
        const loginResult = await login({
          handleRedirect: true,
          inviteId,
          isSignupChangesEnabled,
          onError: () => {
            dispatch({ error: 'Something went wrong.', type: 'error' });
          },
          promo,
          redirect,
          referrer,
          sessionId: session.id,
        });
        if (!loginResult) {
          return;
        }

        clearURLParamData();
        dispatch({ type: 'authComplete' });
      } catch {
        dispatch({ error: 'Authentication failed', type: 'error' });
      }
    };

    processSession();
  }, [
    state.step,
    session,
    isMCPFlow,
    sso.signUp,
    login,
    onNavigateToMCPComplete,
    isSignupChangesEnabled,
    getURLParamData,
    clearURLParamData,
    urlParams,
  ]);

  const handleCompleteProfile = useCallback(
    async (data: { firstName: string; lastName: string }) => {
      dispatch({ type: 'profileSubmitStart' });
      try {
        const updatedSignUp = await updateSignUp(data);
        if (updatedSignUp?.status === 'complete' && updatedSignUp.createdSessionId) {
          await setActive({ session: updatedSignUp.createdSessionId });
          const storedParams = getURLParamData() ?? {};
          const inviteId = urlParams?.inviteId ?? storedParams.inviteId;
          const promo = urlParams?.promo ?? storedParams.promo;
          const referrer = urlParams?.referrer ?? storedParams.referrer;
          const { redirect } = storedParams;
          const loginResult = await login({
            handleRedirect: true,
            inviteId,
            isSignupChangesEnabled,
            onError: () => dispatch({ error: 'Something went wrong.', type: 'error' }),
            promo,
            redirect,
            referrer,
            sessionId: updatedSignUp.createdSessionId,
          });
          if (!loginResult) {
            return;
          }

          clearURLParamData();
          dispatch({ type: 'authComplete' });
        } else {
          throw new Error('Failed to complete sign-up');
        }
      } catch {
        dispatch({ error: 'Something went wrong.', type: 'error' });
      } finally {
        dispatch({ type: 'profileSubmitEnd' });
      }
    },
    [clearURLParamData, getURLParamData, login, setActive, updateSignUp, isSignupChangesEnabled, urlParams],
  );

  const isNewSignUp = sso.signUp?.status === 'complete' || sso.signUp?.status === 'missing_requirements';

  return {
    handleCompleteProfile,
    isNewSignUp,
    state,
  };
};
